import { type MaintenanceInkStroke, type MaintenanceInkText } from '@gb-ticket/shared-types';

export const INK_HISTORY_LIMIT = 30;

export type InkPageState = {
  strokes: MaintenanceInkStroke[];
  texts: MaintenanceInkText[];
};

export type InkPageHistory = {
  past: InkPageState[];
  future: InkPageState[];
};

export function emptyInkState(): InkPageState {
  return { strokes: [], texts: [] };
}

export function emptyInkHistory(): InkPageHistory {
  return { past: [], future: [] };
}

export function cloneInkStrokes(strokes: MaintenanceInkStroke[]): MaintenanceInkStroke[] {
  return strokes.map((stroke) => ({
    points: stroke.points.slice(),
    ...(stroke.color != null ? { color: stroke.color } : {}),
    ...(stroke.width != null ? { width: stroke.width } : {}),
  }));
}

export function cloneInkTexts(texts: MaintenanceInkText[]): MaintenanceInkText[] {
  return texts.map((box) => ({ ...box }));
}

export function cloneInkState(state: InkPageState): InkPageState {
  return { strokes: cloneInkStrokes(state.strokes), texts: cloneInkTexts(state.texts) };
}

export function inkStrokesEqual(
  left: MaintenanceInkStroke[],
  right: MaintenanceInkStroke[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((stroke, index) => {
    const other = right[index];
    if (
      stroke.points.length !== other?.points.length ||
      stroke.color !== other?.color ||
      stroke.width !== other?.width
    ) {
      return false;
    }
    return stroke.points.every(
      (point, pointIndex) =>
        point.x === other?.points[pointIndex]?.x && point.y === other?.points[pointIndex]?.y,
    );
  });
}

export function inkTextsEqual(left: MaintenanceInkText[], right: MaintenanceInkText[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((box, index) => {
    const other = right[index];
    return (
      box.id === other?.id &&
      box.x === other?.x &&
      box.y === other?.y &&
      box.text === other?.text &&
      box.color === other?.color
    );
  });
}

export function inkStatesEqual(left: InkPageState, right: InkPageState): boolean {
  return inkStrokesEqual(left.strokes, right.strokes) && inkTextsEqual(left.texts, right.texts);
}

export function persistableInkTexts(texts: MaintenanceInkText[]): MaintenanceInkText[] {
  return texts.filter((box) => box.text.trim().length > 0);
}

export function pushInkHistory(
  history: InkPageHistory,
  current: InkPageState,
  limit = INK_HISTORY_LIMIT,
): InkPageHistory {
  const past = [...history.past, cloneInkState(current)];
  if (past.length > limit) {
    past.splice(0, past.length - limit);
  }
  return { past, future: [] };
}

export function undoInkHistory(
  history: InkPageHistory,
  current: InkPageState,
): { history: InkPageHistory; next: InkPageState } | null {
  const previous = history.past[history.past.length - 1];
  if (!previous) {
    return null;
  }
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [...history.future, cloneInkState(current)],
    },
    next: cloneInkState(previous),
  };
}

export function redoInkHistory(
  history: InkPageHistory,
  current: InkPageState,
): { history: InkPageHistory; next: InkPageState } | null {
  const following = history.future[history.future.length - 1];
  if (!following) {
    return null;
  }
  return {
    history: {
      past: [...history.past, cloneInkState(current)],
      future: history.future.slice(0, -1),
    },
    next: cloneInkState(following),
  };
}
